import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../i18n';
import PremiereLogo from './PremiereLogo';
import {
  PLATFORM_LABEL,
  PREMIERE_PAGE_URL,
  detectPlatform,
  premiereDownloadLink,
  trackPremiereClick,
  trackPremiereDownload,
} from './premiereExtension';

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400 shrink-0 mt-0.5">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/* Top-of-dashboard card for the Premiere Pro extension. On a phone it only
 * links to the landing page, since the installers are for Mac and Windows. */
const PremiereDashboardCard = () => {
  const { t } = useI18n();
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const other = platform === 'mac' ? 'win' : 'mac';
  const download = useMemo(
    () => (platform ? premiereDownloadLink(platform, 'dashboard') : null),
    [platform],
  );
  const otherDownload = useMemo(
    () => (platform ? premiereDownloadLink(other, 'dashboard_other') : null),
    [platform, other],
  );
  const points = [
    t('Captions land on your timeline, at the start of your clip'),
    t('Only the audio is uploaded, so long clips start fast'),
    t('Same account, plan and credits as here'),
  ];

  return (
    <div className="relative overflow-hidden rounded-[20px] md:rounded-[32px] bg-gradient-to-br from-[#00005b] via-[#0b0b6e] to-[#1a0f4d] p-5 md:p-8 text-white shadow-xl shadow-[#00005b]/20 animate-[fadeIn_0.5s_ease-out]">
      <div className="absolute -top-24 -end-24 w-80 h-80 rounded-full bg-[#9999ff] blur-[110px] opacity-20 pointer-events-none" />
      <div className="absolute -bottom-24 -start-16 w-72 h-72 rounded-full bg-[#f73140] blur-[110px] opacity-15 pointer-events-none" />

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <PremiereLogo className="w-11 h-11 md:w-12 md:h-12" />
            <span className="px-2.5 py-1 rounded-lg bg-white/10 border border-white/15 text-[#c9c9ff] text-[10px] font-extrabold uppercase tracking-wide">
              {t('New · Premiere Pro extension')}
            </span>
          </div>

          <h3 className="text-xl md:text-3xl font-bold tracking-tight leading-tight">
            {t('ArabCap is now inside Premiere Pro')}
          </h3>
          <p className="mt-2 text-[#d6d6ff]/80 font-medium leading-relaxed text-sm md:text-base max-w-2xl">
            {t('Pick a clip, choose the dialect, and get your captions without leaving Premiere. No exporting, no SRT files to import.')}
          </p>

          <ul className="mt-4 space-y-2 text-sm text-white/85 hidden sm:block">
            {points.map((point) => (
              <li key={point} className="flex gap-2.5">
                <CheckIcon />
                {point}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            {platform ? (
              <a
                href={download.href}
                onClick={() => trackPremiereDownload('dashboard', platform, download.eventId)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#00005b] rounded-2xl font-bold text-sm hover:bg-[#e8e8ff] transition-all shadow-lg shadow-black/20 hover:scale-[1.02] active:scale-95"
              >
                <DownloadIcon />
                {t('Download for {os}', { os: PLATFORM_LABEL[platform] })}
              </a>
            ) : (
              <a
                href={PREMIERE_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackPremiereClick('see_more', 'dashboard', null)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#00005b] rounded-2xl font-bold text-sm hover:bg-[#e8e8ff] transition-all shadow-lg shadow-black/20 active:scale-95"
              >
                {t('Get the extension')}
                <span className="inline-block rtl:-scale-x-100">→</span>
              </a>
            )}
            {platform && (
              <a
                href={PREMIERE_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackPremiereClick('see_more', 'dashboard', platform)}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white hover:text-[#c9c9ff] transition-colors"
              >
                {t('See more')}
                <span className="inline-block rtl:-scale-x-100">→</span>
              </a>
            )}
            {platform && (
              <a
                href={otherDownload.href}
                onClick={() => trackPremiereDownload('dashboard_other', other, otherDownload.eventId)}
                className="text-xs font-semibold text-[#c9c9ff]/70 hover:text-white transition-colors sm:ms-auto lg:ms-0"
              >
                {t('Also available for {os}', { os: PLATFORM_LABEL[other] })}
              </a>
            )}
          </div>
        </div>

        <div className="hidden lg:block shrink-0 w-[230px] rotate-[-2deg]">
          <div className="rounded-2xl bg-[#1a1a1a] p-1 shadow-2xl ring-1 ring-white/10">
            <div className="flex items-center gap-1 px-2 pb-1 pt-0.5">
              <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
              <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
              <span className="h-2 w-2 rounded-full bg-[#28c840]" />
            </div>
            <img
              src={`${process.env.PUBLIC_URL}/premiere/panel-editor.webp`}
              alt={t('ArabCap panel in Premiere Pro')}
              width={380}
              height={620}
              loading="lazy"
              className="block w-full rounded-xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiereDashboardCard;
